import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { WalletButtons } from './WalletButtons'
import { apiService } from '../../services/api'
import { config } from '../../config/environment'
import { 
  Download, 
  QrCode, 
  User, 
  MapPin,
  Utensils,
  Phone,
  Radio,
  AlertTriangle,
  Navigation,
  MessageSquare,
  RefreshCw
} from 'lucide-react'

interface MembershipCardData {
  userId: string
  pilotName: string
  pilotNumber: string
  nation: string
  foodPreferences: string
  competitionYear: string
  qrCodeData: string
}

interface MembershipCardProps {
  onError?: (error: string) => void
}

export default function MembershipCard({ onError }: MembershipCardProps) {
  const { user, refreshUserProfile } = useAuth()
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadFormat, setDownloadFormat] = useState<'svg' | 'png'>('png')

  // Refresh profile on mount to get fresh headshotUrl (presigned S3 URL)
  useEffect(() => {
    refreshUserProfile()
  }, [])

  if (!user) {
    return null
  }

  // Build card data from user profile
  const cardData: MembershipCardData = {
    userId: user.userId,
    pilotName: `${user.firstName} ${user.lastName}`,
    pilotNumber: user.participantNumber || user.pilotNumber || 'N/A',
    nation: user.faiNation || user.nation || 'N/A',
    foodPreferences: user.foodChoice || user.foodPreferences || 'None specified',
    competitionYear: user.competitionYear,
    qrCodeData: `${config.portalUrl}/verify/${user.userId}`
  }

  const handleDownload = async (format: 'svg' | 'png') => {
    setIsDownloading(true)
    try {
      // Generate the card file on-demand
      const response = await apiService.generateMembershipCard(format)
      
      if (response.error) {
        onError?.(response.error.message || 'Failed to generate membership card')
        return
      }

      if (response.data) {
        const responseData = response.data as any
        const cardData = responseData.data?.membershipCard || responseData.membershipCard || responseData
        
        if (cardData.downloadUrl) {
          // Download the file
          const link = document.createElement('a')
          link.href = cardData.downloadUrl
          link.download = `fwo-membership-card-${user.competitionYear}.${format}`
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    } catch (error) {
      console.error('Download failed:', error)
      onError?.('Failed to download membership card')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Card Display */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        {/* Header with Logo */}
        <div className="bg-gradient-to-r from-fwo-blue to-blue-600 px-6 py-8 text-white text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/fwo-logo.png" 
              alt="FlyWideOpen" 
              className="h-16" 
            />
          </div>
          <h1 className="text-3xl font-bold mb-2">Pilot ID Card</h1>
          <p className="text-blue-100">Competition Year: {cardData.competitionYear}</p>
        </div>

        {/* Card Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Pilot Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Pilot Details */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3 pb-4 border-b border-gray-200">
                  {/* Headshot photo or placeholder */}
                  {user.headshotUrl ? (
                    <img
                      src={user.headshotUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-20 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                      onError={(e) => {
                        const target = e.currentTarget
                        target.style.display = 'none'
                        const placeholder = target.nextElementSibling as HTMLElement
                        if (placeholder) placeholder.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-20 h-20 rounded-lg bg-gray-100 flex-shrink-0 items-center justify-center ${user.headshotUrl ? 'hidden' : 'flex'}`}
                  >
                    <User className="h-10 w-10 text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Pilot Name</p>
                    <p className="text-2xl font-bold text-gray-900">{cardData.pilotName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start space-x-3">
                    <User className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Pilot Number</p>
                      <p className="text-lg font-semibold text-gray-900">{cardData.pilotNumber}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Nation</p>
                      <p className="text-lg font-semibold text-gray-900">{cardData.nation}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Utensils className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Food</p>
                      <p className="text-lg font-semibold text-gray-900">{cardData.foodPreferences}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contacts Section */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="h-5 w-5 mr-2 text-fwo-blue" />
                  Important Contacts
                </h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Meet Director:</span>
                    <span className="text-fwo-blue font-semibold">+389 7527 2242</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="font-medium">EMERGENCY SERVICES:</span>
                    <span className="text-red-600 font-bold">112</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Radio className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Flying frequency:</span>
                    <span className="font-mono">144.100</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Radio className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Retrieve frequency:</span>
                    <span className="font-mono">144.400</span>
                  </div>
                </div>
              </div>

              {/* Retrieve Instructions */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Navigation className="h-5 w-5 mr-2 text-fwo-blue" />
                  RETRIEVE
                </h3>
                
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start space-x-2">
                    <span className="text-fwo-blue mt-1">•</span>
                    <span>Report back using tracker</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-fwo-blue mt-1">•</span>
                    <span>Walk to tarmac road</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <MessageSquare className="h-4 w-4 text-fwo-blue mt-1" />
                    <span>Interact with Telegram retrieve system</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Radio className="h-4 w-4 text-fwo-blue mt-1" />
                    <span>Keep radio switched on (retrieve frequency)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column - QR Code */}
            <div className="flex flex-col items-center justify-start space-y-4">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(cardData.qrCodeData)}`}
                    alt="Verification QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-center text-sm text-gray-600 mt-3 flex items-center justify-center">
                  <QrCode className="h-4 w-4 mr-1" />
                  Scan for pilot info
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Integration Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add to Digital Wallet</h3>
        <p className="text-gray-600 mb-4 text-sm">
          Save your membership card to your device's digital wallet for quick access.
        </p>
        
        <WalletButtons userId={cardData.userId} />
      </div>

      {/* Download Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Download Card</h3>
        <p className="text-gray-600 mb-4 text-sm">
          Download your membership card as a file for printing or sharing.
        </p>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <select
            value={downloadFormat}
            onChange={(e) => setDownloadFormat(e.target.value as 'svg' | 'png')}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
          >
            <option value="png">PNG Format (Image)</option>
            <option value="svg">SVG Format (Vector)</option>
          </select>
          
          <button
            onClick={() => handleDownload(downloadFormat)}
            disabled={isDownloading}
            className="inline-flex items-center px-6 py-2 bg-fwo-blue text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDownloading ? (
              <>
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Download {downloadFormat.toUpperCase()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}